import {Folio, FolioStatus} from "crqlar-react-common/dist/models/billing/folio.model";
import {FolioItem} from "crqlar-react-common/dist/models/billing/folioItem.model";
import {Service} from "crqlar-react-common/dist/models/service/service.model";

// The folio "Charge" opens: the newest one that isn't void. Expects the list newest first, as
// FolioService.getByReservationId returns it.
export const findLatestNonVoidFolio = (folios?: Folio[]): Folio | undefined =>
    folios?.find(folio => folio.status !== FolioStatus.VOID)

// A reservation's or a reservation group's guests
interface Guests {
    id?: string
    primaryProfileId?: string
    profileIds?: string[]
}

// Whom a folio can be billed to, the primary first: its reservation's guests - a package's folio (it
// has no reservation) its group's - and whom it bills now, like the signing's persons
export const billablePersonIds = (
    folio: Pick<Folio, 'reservationId' | 'reservationGroupId' | 'profileId'>,
    reservation?: Guests,
    packageGroup?: Guests
): string[] => {
    const owner = folio.reservationId
        ? (folio.reservationId === reservation?.id ? reservation : undefined)
        : (folio.reservationGroupId && folio.reservationGroupId === packageGroup?.id ? packageGroup : undefined)
    const ids = [owner?.primaryProfileId, ...(owner?.profileIds ?? []), folio.profileId]
        .filter((id): id is string => !!id?.trim())
    return ids.filter((id, index) => ids.indexOf(id) === index)
}

// The reservation's own line: the item carrying the reservation's service, otherwise the first item
// (items are listed by id ascending, so that's the line created with the folio).
export const findPrimaryFolioItem = (items?: FolioItem[], serviceId?: string): FolioItem | undefined =>
    (serviceId ? items?.find(item => item.serviceId === serviceId) : undefined) ?? items?.[0]

// The line with the no-show fee as its price and, when the sector has a no-show service, that
// service's name, market code and id. Undefined when the line already carries exactly that.
export const withNoShowFee = (
    item: FolioItem,
    fee: number,
    currency: string,
    noShowService?: Service
): FolioItem | undefined => {
    const updated: FolioItem = {
        ...item,
        price: {amount: fee, amountCents: Math.round(fee * 100), currency},
        ...(noShowService && {
            description: noShowService.name || item.description,
            serviceInventoryCode: noShowService.marketCode,
            serviceId: noShowService.id,
        }),
    }

    // null and '' from the backend count as "not set"
    const sameText = (a?: string, b?: string) => (a || '') === (b || '')
    const alreadySaved = item.price?.amountCents === updated.price?.amountCents
        && item.price?.currency === currency
        && sameText(item.description, updated.description)
        && sameText(item.serviceInventoryCode, updated.serviceInventoryCode)
        && sameText(item.serviceId, updated.serviceId)

    return alreadySaved ? undefined : updated
}
